import * as AmazonCognitoIdentity from 'amazon-cognito-identity-js';
import * as jwt from '@/utils/jwt';
import { awsResources } from './resourceParams';
import { userpoolName } from '@/appParams';
import returnCodes from './returnCodeBase';

let poolName: string = userpoolName;
for (const nm of Object.keys(awsResources.userPoolId)) {
  if (nm.indexOf(userpoolName) != 0) continue;
  poolName = nm;
  break;
}
const poolData = {
  UserPoolId: awsResources.userPoolId[poolName],
  ClientId: awsResources.userPoolClientId[poolName],
};

let storageAlreadyChecked = false;
let authInfo: { username: string; idToken: string; refreshToken: string } = { username: '', idToken: '', refreshToken: '' };

const checkToken = (token: string, tolerance = 100): boolean => {
  if (!token) return false;
  const tk: any = jwt.parseJwt(token);
  const timeStamp = Math.round(new Date().getTime() / 1000);
  // console.log("REST of token time: " + (tk.exp - timeStamp));
  return tk.payload.exp > timeStamp + tolerance;
};

/**
 * '
 * @param info '
 * @param info.username '
 * @param info.idToken '
 * @param info.refreshToken '
 */
export const setAuthInfo = (info: { username: string; idToken: string; refreshToken: string }) => {
  authInfo = info;
  if (typeof window !== 'undefined') {
    localStorage.setItem('authInfo', JSON.stringify(authInfo));
  }
  storageAlreadyChecked = true;
};

const clearAuthInfo = () => {
  authInfo = { username: '', idToken: '', refreshToken: '' };
  if (typeof window !== 'undefined') {
    localStorage.removeItem('authInfo');
  }
};

/**
 * '
 * @param update '
 */
export const getAuthInfo = async (update = true): Promise<{ username: string; idToken: string; refreshToken: string } | undefined> => {
  if (typeof window !== 'undefined' && authInfo.username === '' && !storageAlreadyChecked) {
    const ret = localStorage.getItem('authInfo');
    if (ret) {
      authInfo = JSON.parse(ret);
      storageAlreadyChecked = true;
    }
  }
  if (authInfo.username === '' || authInfo.idToken === '') {
    return;
  }

  if (authInfo.idToken !== '' && update && !checkToken(authInfo.idToken)) {
    const res: any = await awsLoginRefresh(authInfo.username, authInfo.refreshToken);
    if (res.success) {
      setAuthInfo({ username: authInfo.username, idToken: res.result.idToken, refreshToken: res.result.refreshToken });
    } else {
      clearAuthInfo();
      return;
    }
  }
  return authInfo;
};

/**
 * AWSログイン機能(リフレッシュ)
 * @param userId AWS CognitoのユーザID.
 * @param refreshToken AWS CognitoのRefresh Token
 * @returns Promise<{ success: boolean, message: Object }> ログイン結果とCognito ID Token/Refresh Token
 */
export const awsLoginRefresh = async (
  userId: string,
  refreshToken: string
): Promise<{ success: boolean; code: number; message: string; result: { idToken: string; refreshToken: string } }> =>
  new Promise((resolve) => {
    if (!userId || !refreshToken) {
      resolve({
        success: false,
        code: returnCodes.auth.invalidToken.code,
        message: 'no param error in updateToken ',
        result: { idToken: '', refreshToken: '' },
      });
      return;
    }
    const rt = new AmazonCognitoIdentity.CognitoRefreshToken({ RefreshToken: refreshToken });
    const cognitoUser = loadCognitoUser(userId);
    cognitoUser.refreshSession(rt, (err: any, result: any) => {
      if (err) {
        setAuthInfo({ username: '', idToken: '', refreshToken: '' });
        resolve({
          success: false,
          code: returnCodes.auth.loginFailed.code,
          message: 'Login Failed',
          result: { idToken: '', refreshToken: '' },
        });
        return;
      }
      const idToken = result.getIdToken().getJwtToken();
      const refreshToken = result.getRefreshToken().getToken();
      setAuthInfo({ username: userId, idToken, refreshToken });
      resolve({ success: true, code: returnCodes.http.success.code, message: 'OK', result: { idToken, refreshToken } });
    });
  });

/**
 * AWS Cognito ID tokenの期限切れをチェックして、必要ならRefresh Tokenでアップデートする
 * @param userId AWS CognitoのユーザID.
 * @param idToken AWS CognitoのID Token
 * @param refreshToken AWS CognitoのRefresh Token
 * @returns Promise<{success: boolean, code: number, message: string, result: ({idToken: string, refreshToken: string})} ログイン結果とCognito ID Token/Refresh Token
 */
export const updateToken = async (
  userId: string,
  idToken: string,
  refreshToken: string
): Promise<{ success: boolean; code: number; message: string; result: { idToken: string; refreshToken: string } }> => {
  if (checkToken(idToken, 100)) {
    return {
      success: true,
      code: returnCodes.http.success.code,
      message: 'OK',
      result: { idToken, refreshToken },
    };
  }
  if (!refreshToken) {
    setAuthInfo({ username: '', idToken: '', refreshToken: '' });
    return {
      success: false,
      code: returnCodes.auth.invalidToken.code,
      message: 'no param error in login ',
      result: { idToken: '', refreshToken: '' },
    };
  }
  return await awsLoginRefresh(userId, refreshToken);
};

/**
 * AWSログイン機能
 * @param userId AWS CognitoのユーザID.
 * @param password AWS CognitoのユーザIDのパスワード.
 * @returns Promise<{ success: boolean, message: Object }> ログイン結果とCognito ID Token/Refresh Token
 */
export const login = async (userId: string, password: string): Promise<{ success: boolean; message: any; code: number; result: any }> =>
  new Promise((resolve) => {
    if (!userId || !password) {
      resolve({
        success: false,
        code: returnCodes.general.invalidCmd.code,
        message: 'no param error in login ',
        result: {},
      });
    }
    const authenticationData = {
      Username: userId,
      Password: password,
    };
    const authenticationDetails = new AmazonCognitoIdentity.AuthenticationDetails(authenticationData);
    const cognitoUser = loadCognitoUser(userId);
    cognitoUser.authenticateUser(authenticationDetails, {
      onSuccess: (result) => {
        const idToken = result.getIdToken().getJwtToken();
        const refreshToken = result.getRefreshToken().getToken();
        setAuthInfo({ username: userId, idToken, refreshToken });
        resolve({ success: true, code: returnCodes.http.success.code, message: 'OK', result: { idToken, refreshToken } });
      },
      newPasswordRequired: (userAttributes: any, requiredAttributes: any) => {
        resolve({
          success: false,
          code: returnCodes.auth.passwordForceChangeRequired.code,
          message: 'password change required',
          result: cognitoUser,
        });
      },
      onFailure: (err) => {
        setAuthInfo({ username: '', idToken: '', refreshToken: '' });
        if (err.code === 'PasswordResetRequiredException') {
          resolve({
            success: false,
            code: returnCodes.auth.passwordRestRequired.code,
            message: 'password reset required',
            result: {},
          });
        } else if (err.code === 'UserNotConfirmedException') {
          resolve({
            success: false,
            code: returnCodes.auth.verifyRequired.code,
            message: 'verify code required',
            result: {},
          });
        } else {
          resolve({ success: false, code: returnCodes.auth.loginFailed.code, message: 'failed login', result: {} });
        }
      },
    });
  });

/**
 * AWSログイン機能(リフレッシュ)
 * @param userId AWS CognitoのユーザID.
 * @param refreshToken AWS CognitoのRefresh Token
 * @returns Promise<{ success: boolean, message: Object }> ログイン結果とCognito ID Token/Refresh Token
 */
export const loginRefresh = async (userId: string, refreshToken: string) =>
  new Promise((resolve) => {
    if (!userId || !refreshToken) {
      resolve({
        success: false,
        code: returnCodes.general.invalidCmd.code,
        message: 'no param error in loginRefresh',
        result: false,
      });
    }
    if (!userId) {
      resolve({ success: false, message: 'userId required' });
      return;
    }
    const rt = new AmazonCognitoIdentity.CognitoRefreshToken({ RefreshToken: refreshToken });
    const cognitoUser = loadCognitoUser(userId);
    cognitoUser.refreshSession(rt, (err, result) => {
      if (err) {
        resolve({ success: false, message: 'login failed' });
        return;
      }
      const idToken = result.getIdToken().getJwtToken();
      const refreshToken = result.getRefreshToken().getToken();
      setAuthInfo({ username: userId, idToken, refreshToken });
      resolve({ success: true, message: { idToken, refreshToken } });
    });
  });

/**
 * AWS パスワードリセット
 * @param userId AWS CognitoのユーザID.
 * @returns Promise<{ success: boolean, message: Object }> 検証結果とCognito ID Token
 */
export const forgetPassword = async (userId: string): Promise<{ success: boolean; code: number; message: string; result: boolean }> =>
  new Promise((resolve) => {
    if (!userId) {
      resolve({
        success: false,
        code: returnCodes.general.invalidCmd.code,
        message: 'no param error in forget password',
        result: false,
      });
    }
    const cognitoUser = createCognitoUser(userId);
    cognitoUser.forgotPassword({
      // onSuccess: function (data) {
      onSuccess() {
        resolve({ success: true, code: returnCodes.http.success.code, message: 'OK', result: true });
      },
      onFailure(err: any) {
        resolve({
          success: false,
          code: returnCodes.auth.forgotPasswordProcessFailed.code,
          message: err.message,
          result: false,
        });
      },
    });
  });

/**
 * AWS パスワード設定
 * @param username AWS CognitoのユーザID.
 * @param verificationCode Verificationコード,
 * @param newPassword 新しいパスワード.
 * @returns Promise<{ success: boolean, message: Object }> 検証結果とCognito ID Token
 */
// eslint-disable-next-line @typescript-eslint/require-await
export const setNewPassword = async (
  username: string,
  verificationCode: string,
  newPassword: string
): Promise<{ success: boolean; code: number; message: string; result: boolean }> =>
  // eslint-disable-next-line no-new
  new Promise((resolve) => {
    if (!username || !verificationCode || !newPassword) {
      resolve({
        success: false,
        code: returnCodes.auth.loginFailed.code,
        message: 'no param error in set new password',
        result: false,
      });
    }
    const cognitoUser = loadCognitoUser(username);
    cognitoUser.confirmPassword(verificationCode, newPassword, {
      onSuccess() {
        resolve({ success: true, code: returnCodes.http.success.code, message: 'OK', result: true });
      },
      onFailure(err: any) {
        resolve({
          success: false,
          code: returnCodes.auth.resetPasswordProcessFailed.code,
          message: err.message,
          result: false,
        });
      },
    });
  });

const createCognitoUser = (userId: string) => {
  const userPool = new AmazonCognitoIdentity.CognitoUserPool(poolData);
  const userData = {
    Username: userId,
    Pool: userPool,
  };
  return new AmazonCognitoIdentity.CognitoUser(userData);
};

const loadCognitoUser = (userId: string) => {
  const userPool = new AmazonCognitoIdentity.CognitoUserPool(poolData);
  const userData = {
    Username: userId,
    Pool: userPool,
  };
  const cognitoUser = userPool.getCurrentUser();
  if (cognitoUser != null) {
    cognitoUser.getSession((err: any, session: any) => {
      if (err) {
        return;
      }
      const cognitoObj: any = {};
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      const key = `cognito-idp.ap-northeast-1.amazonaws.com/${poolData.UserPoolId}`;
      cognitoObj[key] = session.getIdToken().getJwtToken();
    });
  } else {
    return new AmazonCognitoIdentity.CognitoUser(userData);
  }
  return cognitoUser;
};

/**
 *
 */
export const getUserId = (): string => {
  const userPool = new AmazonCognitoIdentity.CognitoUserPool(poolData);
  const cognitoUser: any = userPool.getCurrentUser();
  return cognitoUser?.username;
};

/**
 *
 */
export const logout = async () => {
  const authInfo: any = await getAuthInfo(false);
  if (authInfo == null || authInfo.username === '') {
    return {
      success: true,
      code: returnCodes.http.success.code,
      message: 'OK',
      result: true,
    };
  }

  const userName = jwt.getUserIdFromToken(authInfo.idToken);
  const userPool = new AmazonCognitoIdentity.CognitoUserPool(poolData);
  const userData = {
    Username: userName,
    Pool: userPool,
  };
  // @ts-ignore
  const cognitoUser = new AmazonCognitoIdentity.CognitoUser(userData);
  cognitoUser.signOut();
  clearAuthInfo();
  return {
    success: true,
    code: returnCodes.http.success.code,
    message: 'OK',
    result: true,
  };
};

/**
 * AWSサインアップ機能
 * @param username AWS CognitoのユーザIDとなるメールアドレス
 * @param password AWS CognitoのユーザIDのパスワード.
 * @returns Promise<{ success: boolean, message: Object }> ログイン結果とCognito ID Token
 */
export const signup = async (username: string, password: string): Promise<{ success: boolean; message: string }> =>
  new Promise((resolve) => {
    if (!username || !password) {
      resolve({ success: false, message: 'no param error' });
    }
    const userPool = new AmazonCognitoIdentity.CognitoUserPool(poolData);
    const attributeList: AmazonCognitoIdentity.CognitoUserAttribute[] = [];

    const datausername = {
      Name: 'username',
      Value: username,
    };
    const attributeUsername: AmazonCognitoIdentity.CognitoUserAttribute = new AmazonCognitoIdentity.CognitoUserAttribute(datausername);
    attributeList.push(attributeUsername);
    userPool.signUp(username, password, attributeList, [], (err) => {
      if (err) {
        // alert(err.message || JSON.stringify(err));
        resolve({ success: false, message: 'failed to signUp' });
      }
      resolve({ success: true, message: '' });
    });
  });

/**
 * AWS codeの検証
 * @param username AWS CognitoのユーザIDのメールアドレス.
 * @param code 検証コード.
 * @returns Promise<{ success: boolean, message: Object }> 検証結果とCognito ID Token
 */
export const verify = async (username: string, code: string): Promise<{ success: boolean; message: string }> =>
  new Promise((resolve) => {
    if (!username) {
      resolve({ success: false, message: 'no param error' });
    }
    const cognitoUser = createCognitoUser(username);
    // cognitoUser.confirmRegistration(code, true, function (err, result) {
    cognitoUser.confirmRegistration(code, true, (err) => {
      if (err) {
        // alert(err.message || JSON.stringify(err));
        resolve({ success: false, message: 'the verification code is invalid' });
      }
      // console.log('call result: ' + result);
      resolve({ success: true, message: '' });
    });
  });

/**
 * AWS パスワード設定
 * @param username AWS CognitoのユーザIDとなるメールアドレス
 * @param oldPassword 現在のパスワード
 * @param newPassword 新しいパスワード
 * @returns Promise<{ success: boolean, message: Object }> 検証結果とCognito ID Token
 */
export const changePassword = async (
  username: string,
  oldPassword: string,
  newPassword: string
): Promise<{ success: boolean; message: string }> =>
  new Promise((resolve) => {
    if (!username || !oldPassword || !newPassword) {
      resolve({ success: false, message: 'no param error' });
    }
    const cognitoUser = loadCognitoUser(username);
    // cognitoUser.changePassword(oldPassword, newPassword, function(err, result) {
    cognitoUser.changePassword(oldPassword, newPassword, (err) => {
      if (err) {
        // alert(err.message || JSON.stringify(err));
        // return false;
        resolve({ success: false, message: err.message });
      }
      // console.log('call result: ' + result);
      // return true;
      resolve({ success: true, message: 'OK' });
    });
  });

/**
 * '
 * @param username '
 */
export const awsDelete = (username: string) => {
  const cognitoUser = loadCognitoUser(username);
  cognitoUser.deleteUser((err) => {
    if (err) {
      // alert(err.message || JSON.stringify(err));
      return;
    }
    alert('ご利用いただきありがとうございました');
  });
};

// 別ファイルで取得したcognitoUserを持っておくための変数
// この変数は、使用しなくなるタイミングで必ず初期化処理を行なってください
let cognitoUserObj: any = {};

/**
 * 別ファイルで取得したcognitoUserを受け取り、cognitoUserObjを更新する
 * 引数に何も渡さない場合は空オブジェクトを返す(cognitoUserObjの初期化を行う)
 * @param cognitoUser '
 */
export const setCognitoUser = (cognitoUser?: AmazonCognitoIdentity.CognitoUser): void => {
  if (!cognitoUser) {
    cognitoUserObj = {};
  } else {
    cognitoUserObj = cognitoUser;
  }
};

/**
 * cognitoUserObjを別ファイルで参照する関数
 * @returns '
 */
export const getCognitoUser = (): AmazonCognitoIdentity.CognitoUser => {
  return cognitoUserObj;
};
